import '@adyen/adyen-web/styles/adyen.css';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { ThemeProvider } from '@picsart/growth-rc/theming';
import { withAimStyles } from '@picsart/rc/aim';
import { getCookie } from '@picsart/rc/cookie';
import type { ILocalizationProps } from '@picsart/rc/types/utils/types';
import useMount from '@picsart/rc/useMount';
// import bearerAuthenticationService from '@picsart/growth-rc/bearerAuthenticationService';
import IS_BROWSER from '@picsart/rc/isBrowser';
import HttpService from '@picsart/rc/services/http';
import { LoaderVariants } from '@picsart/design-system/components/Loader';
import { useFoundation } from '@picsart/design-system/foundation/initFoundation';
import { ThemeDarkValues, ThemeLightValues } from '@picsart/design-system/foundation/colors';

import Loader from 'components/Loader';
import NotificationRibbon from 'components/NotificationRibbon';
import WithClientWrapper from 'components/WithClientWrapper';

import ExperimentProvider, { AppContext, CountryContext } from 'context';
import AuthWrapper from 'context/AuthWrapper';
import DriveWrapper from 'context/DriveWrapper';
import PulseProviderWrapper from 'context/PulseProviderWrapper';
import useAnalytics from 'hooks/useAnalytics';
import Analytics from 'services/Analytics';
import errorLogger from 'services/Bugsnag/Bugsnag';
import { API_URL } from 'tmp/endpoints';
import IS_PROD from 'tmp/env';
import { FORBIDDEN_ACTIONS_BY_COUNTRY } from 'tmp/forbiddenCountries';
import endpointConstructor from 'utils/shared/endpointConstructor';
import fetcher from 'utils/shared/fetcher';
import getDomain from 'utils/shared/getDomain';
import isServer from 'utils/shared/isServer';

import useAppStyles from 'styles/app';

import DatadogInit from '../datadog/datadog-init';

const ReactINTLProvider = dynamic<ILocalizationProps>(() =>
  import('@picsart/rc/services/localization').then(mod => mod.ReactINTLProvider),
);
const NotifierProvider = dynamic(() => import('components/NotifierProvider/NotifierProvider'));

const MonetizationWrapper = dynamic(() => import('components/MonetizationWrapper'), { ssr: false });

errorLogger.init();

HttpService.setConfig({
  apiUrl: process.env.NEXT_PUBLIC_API_URL,
});

if (!isServer()) {
  Analytics.init();
}

const ErrorBoundary = errorLogger.getErrorBoundaryProvider();

/**
 * useFoundation({ fonts, themes, writingMode })
 * Generates and applies foundation styles, including fonts, themes, writing modes, and other global styles.
 * */

const themes = [
  {
    key: 'light',
    value: ThemeLightValues,
    selected: true,
  },
  {
    key: 'dark',
    value: ThemeDarkValues,
    selected: false,
  },
];

const App = ({
  shareEnabled,
  likeEnabled,
  countryCode,
  Component,
  pageProps,
  isWebView,
  isMobile,
  isTablet,
  userData,
  tokenData,
  domain,
  ssrLogs,
}) => {
  const isLoggedIn = !!tokenData?.access_token;
  const { events } = useRouter();
  const [loading, setLoading] = useState(false);
  const [openMonetizationPopup, monetizationPopupTrigger] = useState(false);
  const userId = getCookie('gtm_user_id') || null;
  const { pushDataLayer } = useAnalytics();
  useAppStyles();

  if (IS_BROWSER) {
    (window as any).ssrLogs = ssrLogs;
  }

  useEffect(() => {
    const pushUserId = () => {
      pushDataLayer({
        pulse: 'state',
        user_id: userId,
      });
    };
    window.addEventListener('load', pushUserId);
    return () => window.removeEventListener('load', pushUserId);
  }, [pushDataLayer, userId]);

  useFoundation({ themes });

  useMount(() => {
    // integrate tab tracking RC hook
    withAimStyles();

    events.on('routeChangeStart', () => setLoading(true));
    events.on('routeChangeComplete', () => setLoading(false));
    events.on('routeChangeError', () => setLoading(false));

    // Once JS is loaded on the client, components initialized and your JSS styles are regenerated,
    // it's a good time to remove server-side generated style tag to avoid side-effects
    const style = document.getElementById('server-side-styles');
    if (style) {
      style.parentNode.removeChild(style);
    }

    document.documentElement.classList.add(isMobile ? 'mobile' : 'desktop');
  });

  const appContextValue = useMemo(
    () => ({
      isMobile,
      isTablet,
      notifierData: {},
      openMonetizationPopup,
      monetizationPopupTrigger,
    }),
    [isMobile, isTablet, openMonetizationPopup, monetizationPopupTrigger],
  );

  const countryContextValue = useMemo(
    () => ({ code: countryCode, shareEnabled, likeEnabled }),
    [countryCode, shareEnabled, likeEnabled],
  );

  return (
    <ErrorBoundary>
      <DatadogInit />
      <ReactINTLProvider projectName="UGC" language="en">
        <ThemeProvider>
          <AuthWrapper userData={userData} initialTokenData={tokenData}>
            <Loader isLoading={loading} mode={LoaderVariants.Global} />
            <ExperimentProvider>
              <AppContext.Provider value={appContextValue}>
                <CountryContext.Provider value={countryContextValue}>
                  <NotifierProvider>
                    <PulseProviderWrapper>
                      <WithClientWrapper Wrapper={MonetizationWrapper}>
                        <DriveWrapper>
                          <Component
                            isLoggedIn={isLoggedIn}
                            isWebView={isWebView}
                            userData={userData}
                            isMobile={isMobile}
                            isTablet={isTablet}
                            domain={domain}
                            {...pageProps}
                          />
                        </DriveWrapper>
                      </WithClientWrapper>
                    </PulseProviderWrapper>
                  </NotifierProvider>
                </CountryContext.Provider>
              </AppContext.Provider>
              <NotificationRibbon />
            </ExperimentProvider>
          </AuthWrapper>
        </ThemeProvider>
      </ReactINTLProvider>
    </ErrorBoundary>
  );
};

App.getInitialProps = async ({ ctx }) => {
  const HttpService = await import('@picsart/rc/services/http').then(mod => mod.default);
  const { checkDeviceType } = await import('utils/shared/checkDevice');
  const getServerSideCookie = (await import('utils/shared/getCookie')).default;

  const { isMobile, isTablet } = checkDeviceType(ctx.req?.headers['user-agent']);
  const countryCode = (ctx?.req?.headers?.['cf-ipcountry'] || '').toLowerCase();
  const shareEnabled = !FORBIDDEN_ACTIONS_BY_COUNTRY.share[countryCode];
  const likeEnabled = !FORBIDDEN_ACTIONS_BY_COUNTRY.like[countryCode];
  const isLoggedIn = getServerSideCookie(ctx.req?.headers?.cookie, 'user_key');
  const isWebView = ctx?.query?.app === '1';

  const domain = getDomain(ctx.req);
  try {
    const ip =
      ctx?.req?.headers?.['x-forwarded-for']?.split?.(',')[0] ||
      ctx?.req?.headers?.['cf-connecting-ip'] ||
      ctx?.req?.headers['x-real-ip'];

    const body = { refresh_token: getServerSideCookie(ctx.req?.headers?.cookie, 'REFRESH_TOKEN') };

    const COOKIE_DOMAINS_WHITELIST = {
      production: ['picsart.com'],
      stage: ['picsart.com', 'picsartstage2.com', 'picsart.me'],
    };
    const cookieDomains = IS_PROD
      ? COOKIE_DOMAINS_WHITELIST.production
      : COOKIE_DOMAINS_WHITELIST.stage;
    let ssrLogs = {};

    const setRefreshTokenCookie = (response, fetchResponse) => {
      const headerValue = fetchResponse?.headers?.get('set-cookie');
      if (headerValue) {
        const refresh_token = getServerSideCookie(headerValue, 'REFRESH_TOKEN');
        const maxAge = headerValue
          ?.split(';')
          .find(x => x.toLowerCase().includes('max-age'))
          ?.split('=')
          ?.pop();

        ssrLogs = {
          ...ssrLogs,
          refresh_token,
          maxAge,
        };

        if (refresh_token) {
          cookieDomains.forEach(domain => {
            response.cookie('REFRESH_TOKEN', refresh_token, {
              secure: true,
              httpOnly: true,
              sameSite: 'none',
              domain,
              path: '/',
              ...(maxAge && { maxAge: +maxAge }),
            });
          });
        }
      }
    };

    const reqHeaders = {
      'content-type': 'application/json',
      platform: 'website',
      token: process.env.NEXT_PUBLIC_BACKEND_API_TOKEN,
      ...(ip ? { 'client-ip': ip } : {}),
    };

    const tokenData = isLoggedIn
      ? await fetch(`${API_URL}/oauth2/refresh`, {
          headers: reqHeaders,
          credentials: 'include',
          body: JSON.stringify(body),
          method: 'POST',
        })
          .then(res => {
            ssrLogs = {
              ...ssrLogs,
              fetchUrl: `${API_URL}/oauth2/refresh`,
              fetchResponseCookies: res?.headers?.get('set-cookie'),
            };

            setRefreshTokenCookie(ctx.res, res);

            return res.json();
          })
          .then(res => {
            ssrLogs = {
              ...ssrLogs,
              fetchResponse: res.response,
            };
            return res.response;
          })
          .catch((err: unknown) => {
            ssrLogs = {
              ...ssrLogs,
              fetchError: err,
            };
          })
      : undefined;

    // const { getToken } = bearerAuthenticationService(ctx.req, ctx.res);
    // const tokenData = await getToken();
    const cookies = ctx.req?.headers?.cookie;

    HttpService.setConfig({
      apiUrl: process.env.NEXT_PUBLIC_API_URL,
    });

    const deviceid = getServerSideCookie(cookies, 'paa-did');
    let userData: { [key: string]: any } | null = null;

    // get Refresh token from SSR
    // const refreshToken = getServerSideCookie(cookies, 'REFRESH_TOKEN');

    if (tokenData?.access_token && ctx.req) {
      ctx.req.tokenData = tokenData;

      const response = await fetcher(
        endpointConstructor('user', '', { deviceid, accessToken: tokenData.access_token, ip }),
      );
      userData = response;
      ctx.req.userData = response.userData;
    }
    ssrLogs = {
      ...ssrLogs,
      Ip: `ip is ${ip}`,
      ctxIp: ctx?.req?.ip,
      reqHeaders: ctx.req?.headers,
      reqHeaderCookies: ctx.req?.headers?.cookie,
      body,
      refreshTokenSSRCookie: getServerSideCookie(cookies, 'REFRESH_TOKEN'),
      tokenData,
      resHeaders: ctx.res?.getHeaders(),
      reqConnectionRemoteAddress: ctx?.req?.connection?.remoteAddress,
    };

    return {
      shareEnabled,
      countryCode,
      likeEnabled,
      isWebView,
      isMobile,
      isTablet,
      userData,
      tokenData,
      domain,
      ssrLogs,
    };
  } catch (error) {
    console.error('WOWWWW Errorr', { path: 'src/pages/_app', error });
    return {
      tokenData: null,
      isMobile,
      isTablet,
      isWebView,
      countryCode,
      shareEnabled,
      likeEnabled,
      domain,
    };
  }
};

export function reportWebVitals(metric) {
  if (IS_PROD) {
    // eslint-disable-next-line no-console
    console.log(
      `%c ${metric.name} => ${metric.name === 'CLS' ? metric.value : metric.startTime} ms`,
      'color: white; font-weight: bold;font-size: 1rem',
    );
  }
}

export default App;
